import { useEffect, useRef, useState } from 'react';

/**
 * Web Speech API voice dictation. Native to Chrome/Edge/Safari with zero
 * backend cost — the browser does the speech recognition on-device or
 * via the OS speech service depending on platform.
 *
 * Usage:
 *   const voice = useVoiceDictation((appended) => onChange(value + appended));
 *   {voice.supported && <VoiceMicButton voice={voice} />}
 *
 * The hook streams interim transcripts into a ref while the user is
 * speaking, then commits the final transcript to the consumer via the
 * `onCommit` callback when each utterance completes. The callback
 * receives a single space-prefixed string to append to the current
 * value, so the consumer can do `onChange(value + appended)` without
 * worrying about the recording lifecycle.
 */
export interface VoiceDictation {
  supported: boolean;
  listening: boolean;
  /** Live interim transcript while the user is mid-sentence. */
  interim: string;
  start: () => void;
  stop: () => void;
  toggle: () => void;
  /** Last error message, if recognition failed. */
  error: string | null;
}

// SpeechRecognition is webkit-prefixed in Chrome/Safari; unprefixed in some
// browsers. Type as `any` to avoid pulling in DOM lib mismatches.
type SR = any;

function getSpeechRecognitionCtor(): SR | null {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return (
    w.SpeechRecognition ||
    w.webkitSpeechRecognition ||
    w.mozSpeechRecognition ||
    w.msSpeechRecognition ||
    null
  );
}

export function useVoiceDictation(
  onCommit: (appendedText: string) => void,
): VoiceDictation {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  // Keep onCommit current without re-creating the recognizer on every render.
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      // Firefox and most non-Chromium mobile browsers land here.
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';

    rec.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      // event.results is a cumulative list across the recognition session;
      // walk only from `event.resultIndex` to grab what's new since the
      // last onresult call.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }
      if (finalText) {
        // Commit a space-prefixed chunk so it concatenates cleanly with
        // existing field content. Trim leading space if the field is empty
        // and the consumer wants to handle that — easiest to leave as-is.
        const cleaned = finalText.trim();
        if (cleaned) onCommitRef.current(' ' + cleaned);
      }
      setInterim(interimText);
    };

    rec.onerror = (event: any) => {
      // Common errors: 'no-speech', 'audio-capture', 'not-allowed'.
      // Surface a friendly message instead of the raw event.error string.
      const code: string = event?.error ?? 'unknown';
      const msg =
        code === 'not-allowed'
          ? 'Microphone access was denied. Enable it in your browser settings.'
          : code === 'audio-capture'
            ? 'No microphone found. Check that one is connected and selected.'
            : code === 'no-speech'
              ? '' // silence is normal — don't surface anything
              : `Voice input error (${code}).`;
      if (msg) setError(msg);
      setListening(false);
    };

    rec.onend = () => {
      setListening(false);
      setInterim('');
    };

    rec.onstart = () => {
      setError(null);
      setListening(true);
    };

    recognitionRef.current = rec;

    return () => {
      try {
        rec.abort();
      } catch {
        // already stopped — ignore
      }
      recognitionRef.current = null;
    };
  }, []);

  const start = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      rec.start();
    } catch {
      // start() throws if already started — ignore
    }
  };

  const stop = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch {
      // stop() throws if already stopped — ignore
    }
  };

  const toggle = () => {
    if (listening) stop();
    else start();
  };

  return { supported, listening, interim, start, stop, toggle, error };
}

/**
 * Microphone button. Renders nothing if voice dictation is not supported
 * on this browser. Pulses brass while listening, quiet brass when idle.
 */
export function VoiceMicButton({
  voice,
  size = 32,
  ariaLabel = 'Toggle voice input',
}: {
  voice: VoiceDictation;
  size?: number;
  ariaLabel?: string;
}) {
  if (!voice.supported) return null;
  const listening = voice.listening;
  return (
    <>
      <button
        type="button"
        aria-pressed={listening}
        aria-label={ariaLabel}
        onClick={voice.toggle}
        className={`voice-mic-btn${listening ? ' is-listening' : ''}`}
        style={{ width: size, height: size }}
      >
        <svg viewBox="0 0 16 16" width={size * 0.5} height={size * 0.5} aria-hidden="true">
          <path
            d="M8 2a2 2 0 0 0-2 2v4a2 2 0 1 0 4 0V4a2 2 0 0 0-2-2Z"
            fill="currentColor"
          />
          <path
            d="M4 7v1a4 4 0 0 0 8 0V7M8 12v2M5.5 14h5"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <style>{`
        .voice-mic-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 1px solid var(--border-stronger);
          border-radius: 999px;
          color: var(--text-decorative);
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
          transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
        }
        .voice-mic-btn:hover {
          color: var(--brass);
          border-color: var(--brass);
        }
        .voice-mic-btn.is-listening {
          background: var(--brass);
          color: var(--parchment);
          border-color: var(--brass);
          animation: voice-mic-pulse 1.4s ease-in-out infinite;
        }
        @keyframes voice-mic-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(181,133,60,0.5); }
          50% { box-shadow: 0 0 0 6px rgba(181,133,60,0); }
        }
      `}</style>
    </>
  );
}
